import { useState } from 'react';
import {
  Button,
  ButtonGroup,
  InlineStack,
  Panel,
  PanelSection,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAnalytics } from '@/services/analytics';
import { getApiErrorMessage } from '@/utils/errors';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import {
  canSaveSegmentOutSettings,
  shouldEnableOnSave,
  type SegmentOutIntegration,
} from '@/services/db/segment-out-integration/entity';
import {
  useConnectSegmentOutIntegration,
  useDisconnectSegmentOutIntegration,
  useSaveSegmentOutIntegration,
} from '@/services/db/segment-out-integration/mutations';
import { disableStartedProperties, enableStartedProperties } from '../analytics';
import { integrationsOverviewHref } from '../hooks';
import { ConnectForm } from './connect-form';
import { SettingsForm, type SegmentOutSettings } from './settings-form';

type SegmentOutConnectorProps = {
  /** The saved record, or `null` when the workspace has never connected. */
  integration: SegmentOutIntegration | null;
  workspaceId: string;
};

/** What a workspace that has never connected starts from. */
function noSettings(): SegmentOutSettings {
  return { enabled: false, events: [], resolution: false, eu: false, with_content: false, send_cio: false };
}

/**
 * The record's settings, or the blank set when there is no record.
 *
 * Every field is required on the entity — `events` is normalized to a list there
 * — so this only has to drop the fields the form does not edit.
 */
function settingsOf(integration: SegmentOutIntegration | null): SegmentOutSettings {
  if (integration == null) {
    return noSettings();
  }

  const { enabled, events, resolution, eu, with_content, send_cio } = integration;

  return { enabled, events, resolution, eu, with_content, send_cio };
}

function isSameSettings(a: SegmentOutSettings, b: SegmentOutSettings): boolean {
  return (
    a.enabled === b.enabled &&
    a.resolution === b.resolution &&
    a.eu === b.eu &&
    a.with_content === b.with_content &&
    a.send_cio === b.send_cio &&
    a.events.length === b.events.length &&
    a.events.every((event) => b.events.includes(event))
  );
}

/**
 * The connector's panel and its footer actions.
 *
 * The page remounts this on connect or disconnect, so the draft below is seeded
 * once per record rather than synchronised to it.
 */
export function SegmentOutConnector({ integration, workspaceId }: SegmentOutConnectorProps) {
  const { hasPermission } = usePermissions();
  const { track } = useAnalytics();
  const snackbars = useSnackbars();
  const safeNavigate = useSafeNavigate();

  const isReadOnly = !hasPermission('journeys/segment_integration/edit');
  // Connecting and removing are each their own grant. Ember's modern form gated
  // both on edit, so a role given edit alone could set up or tear down a
  // connection it was never granted.
  const canConnect = hasPermission('journeys/segment_integration/create');
  const canRemove = hasPermission('journeys/segment_integration/delete');

  // One id for the read, the three writes, and the cache slot they share.
  const workspace = Number(workspaceId);

  const saved = settingsOf(integration);
  const [draft, setDraft] = useState<SegmentOutSettings>(saved);

  /**
   * Whether a save would also switch the connector on — which is what Ember meant
   * by a "new" integration: one whose *saved* events are still empty. The button
   * says so, and the analytics event distinguishes the first enable from a later one.
   */
  const willEnableOnSave = shouldEnableOnSave({ enabled: draft.enabled, originalEvents: saved.events });

  const connect = useConnectSegmentOutIntegration({
    workspaceId: workspace,
    mutationConfig: {
      // Ember reported the API's own message here (`apiErrors.report`), which is the
      // only place the backend has something specific to say — a rejected write key.
      onError: (error) =>
        snackbars.create({
          variant: 'critical',
          id: 'segment-out-connect.error',
          message: getApiErrorMessage(error, 'Could not connect Segment. Check your write key and try again.'),
        }),
    },
  });

  const save = useSaveSegmentOutIntegration({
    workspaceId: workspace,
    mutationConfig: {
      onSuccess: (updated, submitted) => {
        // Adopt the server's copy only when the form still holds what was sent, so
        // a selection made during the request is not replaced by the older reply.
        setDraft((current) => (isSameSettings(current, submitted) ? settingsOf(updated) : current));
        snackbars.create({
          variant: 'default',
          id: 'segment-out.success',
          message: 'Segment source integration settings saved',
        });
      },
      // Ember rolled the record back here, discarding the edit. Keeping it lets a
      // retry work instead of making the reader redo the selection after a 500.
      onError: () =>
        snackbars.create({
          variant: 'critical',
          id: 'segment-out-save.error',
          message: 'Could not update Segment source integration. Try again.',
        }),
    },
  });

  const disconnect = useDisconnectSegmentOutIntegration({
    workspaceId: workspace,
    mutationConfig: {
      onSuccess: () =>
        snackbars.create({
          variant: 'default',
          id: 'segment-out-delete.success',
          message: 'Segment source integration removed',
        }),
      onError: () =>
        snackbars.create({
          variant: 'critical',
          id: 'segment-out-delete.error',
          message: 'Could not remove Segment source integration. Try again.',
        }),
    },
  });

  // Ember's `canSaveIntegration`: dirty, at least one event, and the edit permission.
  const isDirty = !isSameSettings(draft, saved);
  const canSave = isDirty && canSaveSegmentOutSettings(draft.events) && !isReadOnly;

  return (
    <>
      <Panel withSectionBorders>
        <PanelSection>
          <ConnectForm
            writeKey={integration?.key ?? ''}
            isConnected={integration != null}
            canConnect={canConnect}
            canRemove={canRemove}
            isConnecting={connect.isPending}
            isDisconnecting={disconnect.isPending}
            isSaving={save.isPending}
            onConnect={async (writeKey) => {
              // The error surfaces as a snackbar from the mutation config; swallow the
              // rejection so Pluma's form unlocks instead of staying behind a pending promise.
              await connect.mutateAsync({ key: writeKey }).catch(() => undefined);
            }}
            onDisconnect={() => {
              if (integration == null) return;
              void track('integration', 'integrationDisableStarted', disableStartedProperties('Deleted'));
              disconnect.mutate({ integrationId: integration.id });
            }}
          />
        </PanelSection>

        {integration == null ? null : (
          <SettingsForm
            settings={draft}
            isReadOnly={isReadOnly}
            onChange={setDraft}
            onEnabledChange={(enabled) => {
              void track(
                'integration',
                enabled ? 'integrationEnableStarted' : 'integrationDisableStarted',
                enabled ? enableStartedProperties(saved.events.length === 0) : disableStartedProperties('Disabled'),
              );
            }}
          />
        )}
      </Panel>

      {integration == null ? null : (
        <InlineStack alignInline="end" alignBlock="center" gap="200" mt="200">
          <ButtonGroup groupVariant="spaced">
            <Button variant="tertiary" onClick={() => safeNavigate(integrationsOverviewHref(workspaceId))}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!canSave || disconnect.isPending}
              isLoading={save.isPending}
              onClick={() => {
                // Ember set `enabled` on the record before saving. Moving the draft too — rather
                // than only the outgoing body — is what keeps the toggle showing what was just
                // persisted, and what lets the `onSuccess` guard below compare like with like.
                const submitted = { ...draft, enabled: willEnableOnSave ? true : draft.enabled };

                if (willEnableOnSave) {
                  void track('integration', 'integrationEnableStarted', enableStartedProperties(true));
                }

                setDraft(submitted);
                save.mutate({ integrationId: integration.id, ...submitted });
              }}
            >
              {willEnableOnSave ? 'Save and enable integration' : 'Save integration'}
            </Button>
          </ButtonGroup>
        </InlineStack>
      )}
    </>
  );
}
